// src/components/analysis/NewsPanel.tsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, ExternalLink, Loader2, Newspaper } from 'lucide-react';
import { alpacaNewsService, AlpacaNewsItem } from '@/services/alpacaNewsService';
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from '@/components/ui/button';

interface NewsPanelProps {
  symbol: string;
}

export const NewsPanel: React.FC<NewsPanelProps> = ({ symbol }) => {
  const [news, setNews] = useState<AlpacaNewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSymbol, setCurrentSymbol] = useState<string>(symbol);

  // Update internal state when prop changes
  useEffect(() => {
    if (symbol !== currentSymbol) {
      setCurrentSymbol(symbol);
      setLoading(true);
      setError(null);
    }
  }, [symbol, currentSymbol]);

  // Define fetchNews using useCallback to prevent unnecessary re-creation
  const fetchNews = React.useCallback(async () => {
    if (!currentSymbol) {
      setNews([]);
      setLoading(false);
      setError(null);
      console.log("NewsPanel: No symbol provided, clearing news.");
      return;
    }

    console.log(`NewsPanel: Fetching news for symbol: ${currentSymbol}`);
    setLoading(true);
    setError(null);

    try {
      // Use the new Alpaca news service with fallback
      const data = await alpacaNewsService.getNewsWithFallback(currentSymbol, 5);
      console.log(`NewsPanel: Fetched ${data.length} news articles for ${currentSymbol}:`, data);

      if (Array.isArray(data)) {
        setNews(data);
        if (data.length === 0) {
          console.log(`NewsPanel: No news found for ${currentSymbol}.`);
          // Set a gentle message for no results
          setError(`No recent news found for ${currentSymbol}. This may be due to limited news coverage or API limitations.`);
        }
      } else {
         console.error("NewsPanel: Received non-array data from alpacaNewsService:", data);
         setNews([]);
         setError("Received invalid news data format.");
      }

    } catch (err: any) {
      console.error('NewsPanel: Error fetching news in component:', err);
      const errorMsg = err.message || 'Could not load news. Please try again later.';
      setError(errorMsg);
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [currentSymbol]);

  // Fetch news when the symbol changes
  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Date unavailable';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
          console.warn("Invalid date string received:", dateStr);
          return 'Invalid Date';
      }
      const now = new Date();
      const diffTime = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return diffMinutes < 1 ? 'Just now' : `${diffMinutes}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch (e) {
        console.error("Error formatting date:", dateStr, e);
        return 'Date unavailable';
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex space-x-4">
            <Skeleton className="h-16 w-16 rounded-md flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-2/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center text-red-600 bg-red-50 rounded-lg border border-red-200">
        <AlertCircle className="w-8 h-8 mb-2" />
        <span className="font-medium">News Loading Issue</span>
        <p className="text-sm mt-1 mb-3">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchNews}>
          Try Again
        </Button>
      </div>
    );
  }

  // Content Display
  return (
    <div className="space-y-4">
      {news.length > 0 ? (
        news.map((item, index) => (
          <div
            key={item.id ? `${item.id}-${index}` : `news-${index}`}
            className="border-b border-gray-100 pb-4 last:border-b-0"
          >
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start group gap-3 transition-opacity hover:opacity-80"
            >
              {/* Image */}
              {item.image && (
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                  <img
                    src={item.image}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const parent = e.currentTarget.parentElement;
                      if (parent) parent.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {/* Text Content */}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 mb-1 group-hover:text-blue-600 text-sm sm:text-base line-clamp-2 leading-snug">
                  {item.title || 'Untitled Article'}
                </h3>
                {item.summary && item.summary !== item.title && (
                  <p className="text-xs sm:text-sm text-gray-500 mb-2 line-clamp-2">
                    {item.summary}
                  </p>
                )}
                <div className="flex items-center text-xs text-gray-400 gap-2 flex-wrap">
                  <span className="font-medium truncate max-w-[100px] sm:max-w-[150px] shrink-0">{item.source || 'Unknown Source'}</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="whitespace-nowrap">{formatDate(item.publishedAt)}</span>
                  {/* Sentiment indicator if available */}
                  {item.sentiment !== undefined && item.sentiment !== null && (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <span className={`text-xs px-1 py-0.5 rounded ${
                        item.sentiment > 0.1 ? 'bg-green-100 text-green-800' :
                        item.sentiment < -0.1 ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {item.sentiment > 0.1 ? '📈' : item.sentiment < -0.1 ? '📉' : '⟷'}
                      </span>
                    </>
                  )}
                  <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </a>
          </div>
        ))
      ) : (
        // Empty State
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
          <Newspaper className="w-10 h-10 mx-auto mb-3 text-gray-400" />
          <div className="space-y-2">
            <p className="font-medium">No recent news found for {currentSymbol}</p>
            <p className="text-sm">News is sourced from Alpaca's premium data feed.</p>
            <Button variant="outline" size="sm" onClick={fetchNews} className="mt-3">
              Refresh News
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
